import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

// ── Auth helper ───────────────────────────────────────────────────────────────
async function getIdToken() {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
}

// ── Animated counter ──────────────────────────────────────────────────────────
function useCountUp(target, duration = 1800, start = false) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!start || !target) return;
        let startTime = null;
        const step = (ts) => {
            if (!startTime) startTime = ts;
            const progress = Math.min((ts - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setVal(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
            else setVal(target);
        };
        requestAnimationFrame(step);
    }, [target, start, duration]);
    return val;
}

function useInView(threshold = 0.1) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatPostedAt(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        + ',  ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatEventDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
}

function formatShowDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
}

// ── Feed card ─────────────────────────────────────────────────────────────────
function FeedCard({ item }) {
    const isCreate   = item.feed_type === 'create';
    const label      = isCreate ? 'Event Created' : 'Event Updated';
    const eventPath  = `/events/${item.event_slug || item.event_id}`;
    const festivalPath = item.festival_slug ? `/festivals/${item.festival_slug}` : null;

    return (
        <div style={S.card}>
            <div style={S.cardInner}>
                <div style={S.cardText}>
                    {/* Header */}
                    <div style={S.cardHeader}>
                        <span style={S.feedLabel}>{label}</span>
                        <span style={S.headerSep}> – Posted by </span>
                        <span style={S.postedBy}>{item.posted_by || 'SHARC User'}</span>
                        <span style={S.postedAt}>{formatPostedAt(item.ts)}</span>
                    </div>

                    {/* Event date + venue + location */}
                    <div style={S.eventDate}>{formatEventDate(item.event_date)}</div>
                    <div style={S.metaLine}>
                        {item.venue_slug
                            ? <Link to={`/venues/${item.venue_slug}`} style={S.venueLink}>{item.venue_name}</Link>
                            : <span style={S.venueName}>{item.venue_name}</span>
                        }
                        {(item.venue_city || item.state_name || item.country_name) && (
                            <span style={S.location}>
                                {' '}in {[item.venue_city, item.state_name, item.country_name].filter(Boolean).join(', ')}
                            </span>
                        )}
                    </div>

                    {/* Title */}
                    {item.event_title && (
                        <div style={S.eventTitle}>
                            <Link to={eventPath} style={S.purpleLink}>{item.event_title}</Link>
                        </div>
                    )}

                    {/* Festival */}
                    {festivalPath && (
                        <div style={S.metaLine}>
                            <strong>
                                <Link to={festivalPath} style={S.purpleLink}>{item.festival_name}</Link>
                            </strong>
                        </div>
                    )}

                    {/* Lineup */}
                    {item.lineup && item.lineup.length > 0 && (
                        <div style={S.lineup}>
                            {item.lineup.map((artist, idx) => (
                                <span key={artist.slug || idx}>
                                    {idx > 0 && <span style={S.lineupSep}>, </span>}
                                    {artist.slug
                                        ? <Link to={`/artists/${artist.slug}`} style={S.artistLink}>{artist.name}</Link>
                                        : <strong style={{ color: '#fff' }}>{artist.name}</strong>
                                    }
                                </span>
                            ))}
                        </div>
                    )}

                    {/* View event */}
                    <div style={{ marginTop: '10px' }}>
                        <Link to={eventPath} style={S.viewLink}>View Event →</Link>
                    </div>

                    {/* Actions */}
                    <div style={S.actionRow}>
                        <button style={S.actionBtn} disabled>Like</button>
                        <button style={S.actionBtn} disabled>Comment</button>
                        <button style={S.actionBtn} disabled>Share</button>
                    </div>
                </div>

                {/* Image */}
                {item.image_url && (
                    <div style={S.imageWrap}>
                        <Link to={eventPath}>
                            <div style={S.imageBox}>
                                <img
                                    src={item.image_url}
                                    alt={item.event_title || item.headliner_name || 'Event'}
                                    style={S.cardImage}
                                    onError={e => { e.target.parentNode.parentNode.style.display = 'none'; }}
                                />
                            </div>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Site stat tile (animated) ─────────────────────────────────────────────────
function SiteStat({ value, label, icon, started }) {
    const count = useCountUp(value, 1800, started);
    return (
        <div style={S.siteStatTile}>
            <div style={S.siteStatNumber}>{count.toLocaleString()}</div>
            <div style={S.siteStatLabel}>{label}</div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Home() {
    // ── Feed state ────────────────────────────────────────────────────────────
    const [feedItems,    setFeedItems]    = useState([]);
    const [hasMore,      setHasMore]      = useState(false);
    const [feedOffset,   setFeedOffset]   = useState(0);
    const [feedLoading,  setFeedLoading]  = useState(true);
    const [loadingMore,  setLoadingMore]  = useState(false);
    const [feedError,    setFeedError]    = useState(false);

    // Filter state
    const [countries,   setCountries]   = useState([]);
    const [states,      setStates]      = useState([]);
    const [postedDate,  setPostedDate]  = useState('anytime');
    const [countryId,   setCountryId]   = useState('');
    const [stateId,     setStateId]     = useState('');

    // ── My Shows state ────────────────────────────────────────────────────────
    const [myStats,      setMyStats]      = useState(null);
    const [venueCount,   setVenueCount]   = useState(null);
    const [myShowsUser,  setMyShowsUser]  = useState(null);

    // ── Site stats state ──────────────────────────────────────────────────────
    const [siteStats,    setSiteStats]    = useState(null);
    const [statsRef,     statsInView]     = useInView(0.1);

    // ── Fetch feed ────────────────────────────────────────────────────────────
    const fetchFeed = useCallback(async ({ reset = false } = {}) => {
        try {
            const token = await getIdToken();
            const params = { offset: reset ? 0 : feedOffset, posted_date: postedDate };
            if (countryId) params.country_id = countryId;
            if (stateId)   params.state_id   = stateId;

            const res = await axios.get(`${config.API_URL}/feed`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });

            const { items, has_more, offset: newOffset, filters } = res.data;

            if (reset) setFeedItems(items);
            else       setFeedItems(prev => [...prev, ...items]);

            setHasMore(has_more);
            setFeedOffset(newOffset);
            if (filters?.countries) setCountries(filters.countries);
            if (filters?.states)    setStates(filters.states);
        } catch {
            setFeedError(true);
        } finally {
            setFeedLoading(false);
            setLoadingMore(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedOffset, postedDate, countryId, stateId]);

    // Re-fetch when filters change
    useEffect(() => {
        document.title = 'SHARC - Home';
        setFeedLoading(true);
        setFeedItems([]);
        setFeedOffset(0);
        fetchFeed({ reset: true });
        return () => { document.title = 'SHARC'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postedDate, countryId, stateId]);

    // ── Fetch My Shows summary ────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/user/shows`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const { user, stats, venues_visited } = res.data;
                setMyShowsUser(user);
                setMyStats(stats);
                setVenueCount(venues_visited?.length ?? 0);
            } catch { /* silent */ }
        })();
    }, []);

    // ── Fetch site stats ──────────────────────────────────────────────────────
    useEffect(() => {
        axios.get(`${config.API_URL}/stats`).then(r => setSiteStats(r.data)).catch(() => {});
    }, []);

    const handleCountryChange = (e) => { setCountryId(e.target.value); setStateId(''); };
    const handleLoadMore = () => { setLoadingMore(true); fetchFeed({ reset: false }); };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={S.page}>

            {/* ── Simplified header ─────────────────────────────────────── */}
            <div style={S.pageHeader}>
                <span style={S.pageHeaderPurple}>sharc.fm</span>
                <span style={S.pageHeaderDivider}> | </span>
                <span style={S.pageHeaderSub}>Show Archives</span>
            </div>

            {/* ── Two-column body ───────────────────────────────────────── */}
            <div style={S.body}>

                {/* ── Left: Feed (66%) ──────────────────────────────────── */}
                <div style={S.feedCol}>
                    <h2 style={S.colHeading}>Feed</h2>

                    {/* Filter bar */}
                    <div style={S.filterBar}>
                        <div style={S.filterGroup}>
                            <label style={S.filterLabel}>Posted</label>
                            <select style={S.select} value={postedDate} onChange={e => setPostedDate(e.target.value)}>
                                <option value="anytime">Anytime</option>
                                <option value="24h">Past 24 Hours</option>
                                <option value="week">Past Week</option>
                                <option value="month">Past Month</option>
                            </select>
                        </div>
                        <div style={S.filterGroup}>
                            <label style={S.filterLabel}>Country</label>
                            <select style={S.select} value={countryId} onChange={handleCountryChange}>
                                <option value="">All Countries</option>
                                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        {countryId && (
                            <div style={S.filterGroup}>
                                <label style={S.filterLabel}>Region</label>
                                <select style={S.select} value={stateId} onChange={e => setStateId(e.target.value)}>
                                    <option value="">All Regions</option>
                                    {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Feed items */}
                    {feedLoading && <div style={S.message}>Loading feed...</div>}
                    {!feedLoading && (feedError || feedItems.length === 0) && (
                        <div style={S.message}>No Results Found</div>
                    )}
                    {!feedLoading && feedItems.map((item, idx) => (
                        <FeedCard key={`${item.feed_type}-${item.event_id}-${item.ts}-${idx}`} item={item} />
                    ))}
                    {hasMore && !feedLoading && (
                        <div style={S.loadMoreWrap}>
                            <button style={S.loadMoreBtn} onClick={handleLoadMore} disabled={loadingMore}>
                                {loadingMore ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Right: panels (33%) ───────────────────────────────── */}
                <div style={S.rightCol}>

                    {/* My Shows panel */}
                    <div style={S.panel}>
                        <h3 style={S.panelHeading}>My Shows</h3>

                        {myShowsUser && (
                            <div style={S.userRow}>
                                <div style={S.userAvatar}>
                                    {(myShowsUser.display_name || 'U').charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <div style={S.userName}>{myShowsUser.display_name}</div>
                                    {(myShowsUser.city || myShowsUser.state || myShowsUser.country) && (
                                        <div style={S.userLocation}>
                                            {[myShowsUser.city, myShowsUser.state, myShowsUser.country].filter(Boolean).join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {myStats ? (
                            <>
                                <div style={S.myStatsGrid}>
                                    <div style={S.myStatTile}>
                                        <div style={S.myStatNumber}>{(myStats.total_shows || 0).toLocaleString()}</div>
                                        <div style={S.myStatLabel}>Total Shows</div>
                                    </div>
                                    <div style={S.myStatTile}>
                                        <div style={S.myStatNumber}>{(venueCount || 0).toLocaleString()}</div>
                                        <div style={S.myStatLabel}>Venues Visited</div>
                                    </div>
                                    <div style={{ ...S.myStatTile, ...S.myStatTileWide }}>
                                        <div style={{ ...S.myStatNumber, ...S.myStatNumberPurple }}>
                                            {formatShowDate(myStats.first_show_date)}
                                        </div>
                                        <div style={S.myStatLabel}>First Show</div>
                                    </div>
                                    <div style={{ ...S.myStatTile, ...S.myStatTileWide }}>
                                        <div style={{ ...S.myStatNumber, ...S.myStatNumberPurple }}>
                                            {formatShowDate(myStats.last_show_date)}
                                        </div>
                                        <div style={S.myStatLabel}>Most Recent Show</div>
                                    </div>
                                </div>
                                <Link to="/my-shows" style={S.viewAllLink}>View My Shows →</Link>
                            </>
                        ) : (
                            <div style={S.panelLoading}>Loading...</div>
                        )}
                    </div>

                    {/* Site-Wide Stats panel */}
                    <div style={{ ...S.panel, marginTop: '20px' }} ref={statsRef}>
                        <h3 style={S.panelHeading}>Site-Wide Stats</h3>
                        {siteStats ? (
                            <div style={S.siteStatsGrid}>
                                <SiteStat value={siteStats.artists      || 0} label="Artists"              icon="🎸" started={statsInView} />
                                <SiteStat value={siteStats.venues       || 0} label="Venues"               icon="🏟️" started={statsInView} />
                                <SiteStat value={siteStats.shows        || 0} label="Shows"                icon="🎤" started={statsInView} />
                                <SiteStat value={siteStats.relationships || 0} label="Artist Relationships" icon="🔗" started={statsInView} />
                            </div>
                        ) : (
                            <div style={S.panelLoading}>Loading...</div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        backgroundColor: '#121212',
        color: '#e0e0e0',
        minHeight: '100vh',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        // Escape the 100px side padding App.js <main> applies
        margin: '-32px -100px -32px -100px',
    },

    // ── Page header ──
    pageHeader: {
        textAlign: 'center',
        padding: '28px 100px 20px',
        fontSize: '18px',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        borderBottom: '1px solid #2a2a2a',
    },
    pageHeaderPurple: { color: '#bb86fc', fontWeight: 700 },
    pageHeaderDivider: { color: '#555', fontWeight: 300 },
    pageHeaderSub: { color: '#888', fontWeight: 400 },

    // ── Two-column layout ──
    body: {
        display: 'flex',
        gap: '32px',
        padding: '28px 100px 60px',
        alignItems: 'flex-start',
    },
    feedCol: {
        flex: '2 1 0',
        minWidth: 0,
    },
    rightCol: {
        flex: '1 1 0',
        minWidth: '300px',
        maxWidth: '400px',
        position: 'sticky',
        top: '80px',
        alignSelf: 'flex-start',
    },

    // ── Headings ──
    colHeading: {
        fontSize: '22px',
        fontWeight: 700,
        color: '#e0e0e0',
        marginBottom: '16px',
        marginTop: 0,
    },
    panelHeading: {
        fontSize: '17px',
        fontWeight: 700,
        color: '#e0e0e0',
        marginTop: 0,
        marginBottom: '16px',
        paddingBottom: '10px',
        borderBottom: '1px solid #2e2e2e',
    },

    // ── Filter bar ──
    filterBar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px',
        marginBottom: '20px',
        alignItems: 'flex-end',
    },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
    filterLabel: { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' },
    select: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '13px',
        padding: '6px 10px',
        cursor: 'pointer',
        outline: 'none',
        minWidth: '140px',
    },

    // ── Feed card ──
    card: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        marginBottom: '16px',
        padding: '16px 18px',
    },
    cardInner: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    cardText:  { flex: 1, minWidth: 0 },
    imageWrap: { flexShrink: 0, width: '130px' },
    imageBox: {
        position: 'relative',
        width: '130px',
        paddingBottom: '130px',
        backgroundColor: '#161616',
        borderRadius: '6px',
        border: '1px solid #333',
        overflow: 'hidden',
    },
    cardImage: {
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: '100%',
        objectFit: 'contain', display: 'block',
    },

    // Card header
    cardHeader: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
        gap: '3px', marginBottom: '8px', fontSize: '14px',
    },
    feedLabel:  { color: '#bb86fc', fontWeight: 'bold' },
    headerSep:  { color: '#aaa' },
    postedBy:   { color: '#bb86fc', fontWeight: '600' },
    postedAt:   { color: '#777', fontSize: '12px', marginLeft: '6px' },

    // Card body
    eventDate:  { fontSize: '14px', fontWeight: '600', color: '#e0e0e0', marginBottom: '3px' },
    metaLine:   { fontSize: '13px', color: '#ccc', marginBottom: '3px' },
    venueName:  { color: '#e0e0e0' },
    venueLink:  { color: '#bb86fc', fontWeight: 'bold', textDecoration: 'none' },
    location:   { color: '#aaa' },
    eventTitle: { fontSize: '13px', marginBottom: '3px' },
    lineup: {
        fontSize: '14px', color: '#e0e0e0',
        marginTop: '8px', lineHeight: '1.5', fontWeight: 'bold',
    },
    lineupSep:  { color: '#e0e0e0', fontWeight: 'normal' },
    artistLink: { color: '#ffffff', fontWeight: 'bold', textDecoration: 'none' },
    purpleLink: { color: '#bb86fc', textDecoration: 'none' },
    viewLink:   { color: '#bb86fc', textDecoration: 'none', fontSize: '12px' },

    actionRow: {
        display: 'flex', gap: '14px', marginTop: '10px',
        paddingTop: '10px', borderTop: '1px solid #2e2e2e',
    },
    actionBtn: {
        background: 'transparent', border: 'none',
        color: '#777', fontSize: '13px', cursor: 'not-allowed', padding: 0,
    },

    // ── Right panels ──
    panel: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '18px 20px',
    },

    // User row in My Shows
    userRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
    userAvatar: {
        width: '38px', height: '38px', borderRadius: '6px',
        backgroundColor: '#bb86fc', color: '#121212',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '16px', flexShrink: 0,
    },
    userName:     { fontSize: '15px', fontWeight: 600, color: '#e0e0e0' },
    userLocation: { fontSize: '12px', color: '#888', marginTop: '2px' },

    // My Shows stats grid
    myStatsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        marginBottom: '14px',
    },
    myStatTile: {
        backgroundColor: '#2a2a2a',
        borderRadius: '6px',
        border: '1px solid #333',
        padding: '12px 10px',
        textAlign: 'center',
    },
    myStatTileWide: { gridColumn: 'span 1' },
    myStatNumber: {
        fontSize: '20px', fontWeight: 800, color: '#e0e0e0',
        letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: '4px',
    },
    myStatNumberPurple: { fontSize: '14px', fontWeight: 700, color: '#bb86fc' },
    myStatLabel: { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em' },

    viewAllLink: {
        display: 'block', textAlign: 'center',
        color: '#bb86fc', textDecoration: 'none',
        fontSize: '13px', marginTop: '4px',
    },

    // Site-Wide Stats grid
    siteStatsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
    },
    siteStatTile: {
        backgroundColor: '#2a2a2a',
        borderRadius: '6px',
        border: '1px solid #333',
        padding: '14px 10px',
        textAlign: 'center',
    },
    siteStatIcon:   { fontSize: '18px', marginBottom: '6px' },
    siteStatNumber: {
        fontSize: '22px', fontWeight: 800, color: '#bb86fc',
        letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '4px',
        fontVariantNumeric: 'tabular-nums',
    },
    siteStatLabel: { fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 500 },

    // ── Misc ──
    message: { color: '#888', fontSize: '14px', textAlign: 'center', padding: '40px 0' },
    panelLoading: { color: '#666', fontSize: '13px', textAlign: 'center', padding: '20px 0' },
    loadMoreWrap: { textAlign: 'center', marginTop: '16px' },
    loadMoreBtn: {
        backgroundColor: '#2c2c2c', border: '1px solid #444',
        borderRadius: '4px', color: '#e0e0e0',
        fontSize: '13px', padding: '8px 24px', cursor: 'pointer',
    },
};
